import React from 'react';
import styled, { css } from 'styled-components';

import { BasketState } from '@front/common/src/ducks/common/basket';
import { formatPrice, formatPricePerHour } from '@front/common/src/utils/formatting';
import colors from '@front/common/src/utils/colors';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { MembershipOptIn } from '@front/customer/src/modals/membership-paywall/utils';
import { useInstantTaxCreditCleaningFunnelVariant } from '@front/customer/src/hooks/use-instant-tax-credit-cleaning-funnel-variant';
import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';
import { PricesResponse } from '@front/customer/src/types/prices';

import { translate } from '@front/i18n';

import Text from '@front/web-ui/src/components/base-text/variants/text';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import SelectablePriceCard, { SelectablePriceCardProps } from '../selectable-price-card';

const t = translate('modals.membership_paywall.price_cards');

const StyledCard = styled(SelectablePriceCard)<{
  $height: 'md' | 'lg';
}>`
  ${({ $height }) => css`
    height: ${$height === 'lg' ? 105 : 85}px;
  `};
`;

const StyledPrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

type SelectableCleaningMemberPriceCardProps = Pick<SelectablePriceCardProps, 'onPress'> & {
  duration: BasketState['duration'];
  prices: PricesResponse | undefined;
  isLoadingPrices: boolean;
  optInChoice?: MembershipOptIn;
};

const SelectableCleaningMemberPriceCard = ({
  duration,
  onPress,
  prices,
  isLoadingPrices,
  optInChoice,
}: SelectableCleaningMemberPriceCardProps) => {
  const currentCountry = getCurrentCountry();

  const showTaxReductions = useInstantTaxCreditCleaningFunnelVariant({ universe: 'cleaning' });

  const showPrices = !!duration;
  const formattedReduction = showPrices
    ? formatPrice(prices?.member.discount.price)
    : formatPricePerHour(MEMBERSHIP_CONFIG_BY_COUNTRY[currentCountry].cleaningMaxPricePerHour, { precision: 0 });

  return (
    <StyledCard
      $height={showTaxReductions ? 'lg' : 'md'}
      isSelected={optInChoice === 'membership'}
      onPress={onPress}
      title={t('.member.title')}
      label={t('.member.label', { memberReduction: formattedReduction })}
      value='membership'
    >
      {showPrices && (
        <StyledPrices>
          {isLoadingPrices ? (
            <Skeleton linesWidth={showTaxReductions ? ['70px', '90px'] : ['70px']} />
          ) : (
            <>
              <Text strikedOut>{formatPrice(prices?.standard.after_tax.price)}</Text>
              <Text.MDBold color={colors.pink120}>{formatPrice(prices?.member.after_tax.price)}</Text.MDBold>
              {showTaxReductions && (
                <>
                  <Text.MDBold color={colors.pink120}>
                    {t('.cleaning.or_price', { priceBeforeITC: formatPrice(prices?.member.total.price) })}
                  </Text.MDBold>
                  <Text.XXS>{t('.cleaning.before_itc')}</Text.XXS>
                </>
              )}
            </>
          )}
        </StyledPrices>
      )}
    </StyledCard>
  );
};

export default SelectableCleaningMemberPriceCard;
